import React from 'react';
import { Nullable } from 'global';
import { useWatch } from 'react-hook-form';
import { UseFormReturn } from 'react-hook-form/dist/types/form';
import { ListingForm } from 'components/shared/TableListing/types';
import { Button, Stack, Typography, Tooltip } from '@mui/material';
import { AddCircle, Save, HighlightOff, ControlPoint } from '@mui/icons-material';
import { ButtonsSkeleton } from '../components/ActionsSkeleton';
import { UpdateCriteriaStatusVariables } from './api';
import { TierTableContext, TierEntity, TierCriteria } from './types';
import { RenderWithFallback } from '../../../components/shared/Conditional/RenderWithFallback';

interface Props {
  isLoading?: boolean;
  isUpdating?: boolean;
  criteria: TierCriteria;
  tierId: Nullable<string>;
  isUpdatingStatus: boolean;
  onAddCondition: VoidFunction;
  form: UseFormReturn<ListingForm<TierEntity, TierTableContext>>;
  onSubmit: (formData: ListingForm<TierEntity, TierTableContext>) => void;
  onChangeCriteriaStatus: (updateDTO: UpdateCriteriaStatusVariables['input']) => Promise<void>;
}

export const ActionPanel = ({
  form,
  tierId,
  onSubmit,
  criteria,
  isUpdating,
  onAddCondition,
  isUpdatingStatus,
  isLoading = false,
  onChangeCriteriaStatus,
}: Props) => {
  const bodyFields = useWatch({ control: form.control, name: 'bodyFields' });
  const isSaveBtnActive = bodyFields?.some(({ criteria }) => !!criteria);

  return (
    <Stack gap="1.5rem">
      <Stack direction="row" justifyContent="space-between" gap="1rem">
        <Typography variant="h6" component="span">
          Internal Tiers
        </Typography>

        <Stack direction="row" justifyContent="flex-end" gap="1rem">
          <RenderWithFallback fallBack={<ButtonsSkeleton />} isLoading={isLoading}>
            {tierId && (
              <Button
                variant="contained"
                loadingPosition="start"
                loading={isUpdatingStatus}
                color={criteria.isActive ? 'error' : 'success'}
                startIcon={criteria.isActive ? <HighlightOff /> : <ControlPoint />}
                onClick={() =>
                  onChangeCriteriaStatus({
                    active: !criteria.isActive,
                    financeProgramId: criteria.id!,
                  })
                }
              >
                {criteria.isActive ? 'Deactivate' : 'Activate'}
              </Button>
            )}

            <Tooltip title={isSaveBtnActive ? '' : 'Please add criteria to save'}>
              <span>
                <Button
                  variant="contained"
                  loading={isUpdating}
                  startIcon={<Save />}
                  loadingPosition="start"
                  disabled={!isSaveBtnActive}
                  onClick={form.handleSubmit(onSubmit)}
                >
                  {tierId ? 'Update ' : 'Save'}
                </Button>
              </span>
            </Tooltip>
          </RenderWithFallback>
        </Stack>
      </Stack>

      <Stack direction="row" justifyContent="flex-end">
        <Button size="small" disabled={isLoading} startIcon={<AddCircle />} onClick={onAddCondition}>
          Add criteria
        </Button>
      </Stack>
    </Stack>
  );
};
